import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Textarea,
  useColorMode,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import { useTranslation } from 'react-i18next';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';

import Plugin, { PluginProps } from '../Plugin';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import getStudents from '../../../helpers/getStudents';
import { trackAction } from '../../../../../helpers/trackAction';
import { fullNameUsernameOrEmail } from '../../../../../helpers/username';
import timeAgo from '../../../../../helpers/timeAgo';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import { PluginType } from '../../../types/JsonCmsContent';
import TriggerBlock from './TriggerBlock';
import Markdown from '../../../../../components/Markdown';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './constants';
import useTeacherStudyGroup from '../../../hooks/useTeacherStudyGroup';
import useInteractiveQuizAnswersFromActiveLicenses from '../../../hooks/useInteractiveQuizAnswersFromActiveLicenses';
import AdminInteractiveQuizAnswers from './AdminInteractiveQuizAnswers';
import { colors } from '../../../../../App/ChakraTheme';

type StudentAnswerProps = {
  isCorrect: boolean;
  manuallyGraded: boolean;
  points: number;
  maxPoints: number;
  date: Date;
  student: any;
  answers: any;
  allAnswers: { answer: string; isCorrect: boolean }[];
  questionType: string;
  getOldAnswers: () => void;
  questionId: string;
  studyGroupId: string;
  studentId: string;
  answerId: string;
};

const StudentAnswer: React.FC<StudentAnswerProps> = (props) => {
  const {
    isCorrect,
    points,
    maxPoints,
    date,
    student,
    answers,
    allAnswers,
    manuallyGraded,
    questionType,
    getOldAnswers,
    questionId,
    studyGroupId,
    studentId,
    answerId,
  } = props;
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const [gradePoints, setGradePoints] = useState<number>(points);
  const toast = useToast();

  let isString = typeof answers == 'string';

  const needsGrading = !manuallyGraded && questionType === 'FreeForm';

  const gradeAnswer = () => {
    axios
      .patch(
        '/api/v1/syllabus/interactive_quiz_answers/',
        {
          question_id: questionId,
          study_group_id: studyGroupId,
          student_id: studentId,
          answer_id: answerId,
          points: gradePoints,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {
        toast({
          title: t('plugin.interactiveQuiz.success'),
          description: t('plugin.interactiveQuiz.gradedAnswer'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        getOldAnswers();
      })
      .catch((error) => {
        console.log(error)
        toast({
          title: t('plugin.interactiveQuiz.error'),
          description: error,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        console.error(error);
      });
  };

  return (
    <VStack
      p={2}
      // backgroundColor={
      //   isCorrect
      //     ? colors.applyOpacity(colors.limegreen, 0.1)
      //     : colors.applyOpacity(colors.crimson, 0.1)
      // }
      borderRadius="2xl"
      justify="start"
      align="start"
      spacing={0}
      gap={1}
    >
      <HStack
        w="full"
        justify="start"
        spacing={0}
        gap={4}
        justifyContent="space-between"
      >
        <Text
          minW="450px"
          as="div"
          p={0}
          m={0}
          display="flex"
          alignItems="center"
        >
          {fullNameUsernameOrEmail(student)}
          &nbsp;
          {needsGrading ? (
            <Text as="span" color="#4e06b3" fontSize="14px">
              {t('plugin.interactiveQuiz.notGradedYet')}
            </Text>
          ) : (
            <>
              {isCorrect ? '✅' : '❌'}
              &nbsp;
              <Text
                as="span"
                fontSize="14px"
              >{`(${points} / ${maxPoints})`}</Text>
            </>
          )}
        </Text>
        <Tippy
          content={`${date.toLocaleDateString()} ${date.toLocaleTimeString()}`}
        >
          <Text as="div">{timeAgo(date)}</Text>
        </Tippy>
      </HStack>
      <HStack
        w="full"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        gap={4}
      >
        <VStack align="start" justify="start" spacing={2} gap={2} w="full">
          {isString ? (
            <Text w="full" boxShadow="md" py={2} px={4} whiteSpace="pre-wrap" wordBreak="break-word">
              {answers}
            </Text>
          ) : (
            Object.entries(allAnswers).map(([key, value]: any, index) => {
              const nkey = Number(key);
              const wasSelected = answers.includes(nkey);
              let isUserCorrect = null;
              if (wasSelected && value.isCorrect) {
                isUserCorrect = true;
              } else if (wasSelected && !value.isCorrect) {
                isUserCorrect = false;
              } else if (!wasSelected && value.isCorrect) {
                isUserCorrect = false;
              }
              return (
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  boxShadow="md"
                  px={5}
                  py={3}
                  key={index}
                  w="full"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  style={{
                    ...(isUserCorrect === null
                      ? {}
                      : isUserCorrect
                        ? {
                          background: 'rgba(117,214,64,1)',
                          color: 'white',
                          borderColor: 'rgba(117,214,64,1)',
                          fontWeight: '600',
                        }
                        : wasSelected
                          ? {
                            background: 'rgba(220,53,69,1)',
                            color: 'white',
                            borderColor: 'rgba(220,53,69,1)',
                            fontWeight: '600',
                            borderWidth: '2px',
                          }
                          : {
                            background: isDark ? '#1e293b' : 'white',
                            color: isDark ? '#f1f5f9' : 'black',
                            borderColor: 'rgba(117,214,64,1)',
                            fontWeight: '600',
                            borderWidth: '2px',
                          }),
                  }}
                >
                  {value.answer}
                </Box>
              );
            })
          )}
        </VStack>
        <HStack
          justifyContent="space-between"
          alignItems="center"
          spacing={0}
          gap={4}
        >
          <NumberInput
            allowMouseWheel
            maxWidth="80px"
            defaultValue={points}
            min={-1}
            max={maxPoints}
            onChange={(val) => setGradePoints(Number(val))}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Text as="span">/ {maxPoints}</Text>
          <Button colorScheme="blue" variant="solid" onClick={gradeAnswer}>
            {t('plugin.interactiveQuiz.grade')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

type AnswerOptionProps = {
  isSelected: boolean;
  onClick: () => void;
  isUserCorrect: boolean | null;
  wasSelected: boolean | null;
  showFeedback: boolean;
  isDisabled: boolean;
  isMulti: boolean;
  children: React.ReactNode;
};

const ANSWER_STATES = {
  default: {
    bg: 'white',
    color: 'gray.800',
    borderColor: 'gray.300',
    borderWidth: '2px',
    fontWeight: '400' as const,
  },
  selected: {
    bg: 'blue.50',
    color: 'blue.800',
    borderColor: 'blue.500',
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
  correct: {
    bg: colors.limegreen,
    color: 'white',
    borderColor: colors.limegreen,
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
  incorrectSelected: {
    bg: 'red.500',
    color: 'white',
    borderColor: 'red.600',
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
};

const ANSWER_STATES_DARK = {
  default: {
    bg: '#1e293b',
    color: '#f1f5f9',
    borderColor: '#475569',
    borderWidth: '2px',
    fontWeight: '400' as const,
  },
  selected: {
    bg: '#1e3a5f',
    color: '#90cdf4',
    borderColor: '#3b82f6',
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
  correct: {
    bg: colors.limegreen,
    color: 'white',
    borderColor: colors.limegreen,
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
  incorrectSelected: {
    bg: 'red.500',
    color: 'white',
    borderColor: 'red.600',
    borderWidth: '2px',
    fontWeight: '600' as const,
  },
};

const AnswerOption: React.FC<AnswerOptionProps> = ({
  isSelected,
  onClick,
  isUserCorrect,
  wasSelected,
  showFeedback,
  isDisabled,
  isMulti,
  children,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const states = isDark ? ANSWER_STATES_DARK : ANSWER_STATES;

  const handleClick = () => {
    if (!isDisabled) onClick();
  };

  const getStateStyles = () => {
    if (showFeedback && isUserCorrect !== null) {
      if (isUserCorrect) return states.correct;
      if (wasSelected) return states.incorrectSelected;
      return states.default;
    }
    if (isSelected) return states.selected;
    return states.default;
  };

  const styles = getStateStyles();

  return (
    <Box
      role={isMulti ? 'checkbox' : 'radio'}
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      borderRadius="lg"
      boxShadow="sm"
      w="full"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
      px={5}
      py={3}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      bg={styles.bg}
      color={styles.color}
      borderWidth={styles.borderWidth}
      borderColor={styles.borderColor}
      fontWeight={styles.fontWeight}
      transition="all 0.15s ease"
      opacity={isDisabled ? 1 : undefined}
      _hover={!isDisabled ? {
        borderColor: isSelected ? 'blue.600' : isDark ? '#64748b' : 'gray.400',
        boxShadow: 'md',
      } : undefined}
      _focus={!isDisabled ? {
        outline: '2px solid',
        outlineColor: 'blue.400',
        outlineOffset: '2px',
      } : undefined}
    >
      <HStack spacing={3} align="center">
        <Box
          flexShrink={0}
          w={5}
          h={5}
          borderRadius={isMulti ? 'sm' : 'full'}
          borderWidth="2px"
          borderColor={
            showFeedback && isUserCorrect !== null
              ? isUserCorrect
                ? colors.limegreen
                : wasSelected
                  ? 'red.500'
                  : 'gray.300'
              : isSelected
                ? 'blue.500'
                : 'gray.300'
          }
          bg={
            showFeedback && isUserCorrect !== null
              ? isUserCorrect
                ? colors.limegreen
                : wasSelected
                  ? 'red.500'
                  : 'transparent'
              : isSelected
                ? 'blue.500'
                : 'transparent'
          }
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {(showFeedback && (isUserCorrect === true || wasSelected === true)) ||
          (!showFeedback && isSelected) ? (
            <Text
              as="span"
              color="white"
              fontSize="xs"
              fontWeight="bold"
              lineHeight={1}
            >
              {showFeedback && wasSelected && !isUserCorrect ? '✕' : '✓'}
            </Text>
          ) : null}
        </Box>
        <Text as="span" flex={1} fontSize="md">
          {children}
        </Text>
      </HStack>
    </Box>
  );
};

const InteractiveQuiz = (props: PluginProps) => {
  const { content, studyGroup } = props;
  const {
    students: studentsFromTeacherStudyGroups,
    teacherStudyGroups,
  } = useTeacherStudyGroup();
  const { t } = useTranslation();
  const qStudentId = useQueryParam('student-id');
  const qQuizId = useQueryParam('quiz-id');
  const contentData = content.data;
  const questionType = contentData.question_type;
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [disableAnswer, setDisableAnswer] = useState(false);
  const user = useContext(AuthContext);
  const [textareaValue, setTextareaValue] = useState('');
  const [textareaDisabled, setTextareaDisabled] = useState(false);
  const [studentId, setStudentId] = useState<string | null>(qStudentId);
  const [students, setStudents] = useState<any[]>([]);
  const [studentAnswers, setStudentAnswers] = useState<any>([]);
  const isTeacher = user?.roles?.includes(Role.teacher);
  const isStaff = user?.roles?.includes(Role.staff);
  const [showStudentAnswers, setShowStudentAnswers] = useState(
    (isTeacher) && qQuizId === String(contentData.id),
  );
  const [showAllSchools, setShowAllSchools] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const isMobile = useIsMobile();
  const currentQuestionId = contentData.unique_quiz_id;
  const isMulti = contentData.choice_type === 'MultiChoice';
  const repeatCount = contentData.repeat_count;
  const correctAnswers: any[] = [];
  const asnwerMap: Record<string, { answer: string; is_correct: boolean }> = {};
  Object.entries(contentData.answers).forEach(([key, value]: any) => {
    if (value.isCorrect) {
      correctAnswers.push(key);
    }
    asnwerMap[key] = value;
  });

  const { quizAnswers } = useInteractiveQuizAnswersFromActiveLicenses(currentQuestionId)

  const INCORRECT_ANSWER_RENDER = (
    <Text display="inline" color="rgba(220,53,69,1)">
      {t('plugin.interactiveQuiz.incorrect')}
    </Text>
  );

  const PARTIALLY_CORRECT_ANSWER_RENDER = (
    <Text display="inline" color="rgba(255,193,7,1)">
      {t('plugin.interactiveQuiz.partiallyCorrect')}
    </Text>
  );

  const CORRECT_ANSWER_RENDER = (
    <Text display="inline" color={colors.limegreen}>
      {t('plugin.interactiveQuiz.correct')}
    </Text>
  );

  const checkStudentAnswers = (
    studentAnswers: {
      model: string;
      pk: number;
      fields: {
        answer_id: string;
        student_id: string;
        study_group_id: string;
        question_id: string | null;
        unique_quiz_id: string;
        answers: string;
        is_correct: boolean;
        points: number;
        manually_graded: boolean;
        created_at: string;
        ai_grade: string;
      };
    },
    allAnswers: Record<string, { answer: string; isCorrect: boolean }>,
  ) => {
    const userAnswers = studentAnswers.fields.answers.split(',');
    const allAnswersKeys = Object.keys(allAnswers);

    const correctAnswersKeys = Object.keys(allAnswers).filter(
      (key) => allAnswers[key]?.isCorrect,
    );
    const incorrectAnswersKeys = Object.keys(allAnswers).filter(
      (key) => !allAnswers[key]?.isCorrect,
    );

    // if user has incorrect answer
    if (
      userAnswers.some((answer: string) =>
        incorrectAnswersKeys.includes(answer),
      )
    ) {
      return INCORRECT_ANSWER_RENDER;
    }

    // if user checked all answers to trick quiz and not all answers are correct
    if (
      allAnswersKeys.length !== correctAnswersKeys.length &&
      userAnswers.length === allAnswersKeys.length
    ) {
      return INCORRECT_ANSWER_RENDER;
    }

    // if user answers are same as correct answers
    if (
      userAnswers.length === correctAnswersKeys.length &&
      correctAnswersKeys.every((key) => userAnswers.includes(key))
    ) {
      return CORRECT_ANSWER_RENDER;
    }

    return PARTIALLY_CORRECT_ANSWER_RENDER;
  };

  const studentOptions = React.useMemo(() => {
    return students.map((s) => ({
      value: s.pk,
      label: fullNameUsernameOrEmail(s),
    }));
  }, [students]);

  const userAnswers = studentAnswers.filter((answer: any) => {
    return answer.fields.student_id === user?.pk;
  });

  const saveAnswer = () => {
    let isUserCorrect = true;
    let correctCount = 0;
    correctAnswers.forEach((correctAnswer) => {
      if (!selectedAnswers.includes(correctAnswer)) {
        isUserCorrect = false;
      } else {
        correctCount += 1;
      }
    });
    if (selectedAnswers.length !== correctAnswers.length) {
      isUserCorrect = false;
    }
    const userPercentage =
      correctCount / Math.max(correctAnswers.length, selectedAnswers.length);
    const userAnswer: {
      isUserCorrect: boolean;
      selectedAnswers: string[];
    } = {
      isUserCorrect,
      selectedAnswers,
    };
    trackAction({
      action_type: 'answered_quiz',
      action_object: {
        type: 'content',
        pk: contentData.pk,
      },
      study_group: { pk: studyGroup.pk },
      data: {
        questionId: currentQuestionId,
        correct: userAnswer.isUserCorrect,
        answers:
          questionType === 'FreeForm'
            ? textareaValue
            : userAnswer.selectedAnswers.join
              ? userAnswer.selectedAnswers.join(',')
              : userAnswer.selectedAnswers,
      },
    });
    setIsSaving(true);
    axios
      .post(
        '/api/v1/syllabus/interactive_quiz_answers/',
        {
          question_id: currentQuestionId,
          study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
          answers:
            questionType === 'FreeForm'
              ? textareaValue
              : userAnswer.selectedAnswers.join
                ? userAnswer.selectedAnswers.join(',')
                : userAnswer.selectedAnswers,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {
        if (questionType === 'FreeForm') {
          toast({
            title: t('plugin.interactiveQuiz.success'),
            description: t('plugin.interactiveQuiz.gradedAnswer'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        getOldAnswers();
      })
      .catch((error) => {
        console.error(error);
        toast({
          title: t('plugin.interactiveQuiz.errorSavingAnswer'),
          description: error,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => {
        setShowCorrectAnswers(true);
        setIsSaving(false);
      });
  };

  const getOldAnswers = () => {
    if (isTeacher) {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
            question_id: currentQuestionId,
            get_study_group_data: true,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          setStudentAnswers(JSON.parse(response.data));
        })
        .catch((e) => console.error(e));
    } else {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk || CHALLENGE_VIRTUAL_STUDY_GROUP_ID,
            question_id: currentQuestionId,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          const oldAnswersData = JSON.parse(response.data);
          setStudentAnswers(oldAnswersData);
          if (oldAnswersData.length >= repeatCount && repeatCount !== -1) {
            setDisableAnswer(true);
            setShowCorrectAnswers(true);
            setTextareaDisabled(true);
          } else if (oldAnswersData.length > 0 && questionType === 'ABCDQuestion') {
            setShowCorrectAnswers(true);
          }
          if (questionType === 'FreeForm') {
            if (oldAnswersData.length) {
              setTextareaValue(
                oldAnswersData[oldAnswersData.length - 1].fields.answers,
              );
            }
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  useEffect(() => {
    if (isStaff) {
      // we are handling staff differently - showing all students
      return
    }

    if (isTeacher) {
      if (studyGroup?.pk) {
        getStudents(studyGroup.pk).then(setStudents);
      } else if (studentsFromTeacherStudyGroups?.length) {
        setStudents(studentsFromTeacherStudyGroups);
      }
    }

    if (studyGroup || teacherStudyGroups?.length) {
      getOldAnswers();
    }

    return () => {
      setStudentAnswers([]);
    }
  }, [studyGroup, studentsFromTeacherStudyGroups]);

  useEffect(() => {
    if (!showStudentAnswers) {
      setShowStudentAnswers(
        (isTeacher || isStaff) && qQuizId === String(contentData.unique_quiz_id),
      );
      setTimeout(() => {
        const el = document.querySelector(
          `.question-${qQuizId}`,
        ) as HTMLElement;
        if (el) {
          const offset =
            (document.querySelector('.content-controls') as HTMLElement)
              ?.clientHeight || 0;
          window.scrollTo({ top: el.offsetTop - offset, behavior: 'smooth' });
        }
      }, 400);
    }
  }, [user, contentData]);

  const onChange = (v: any) => {
    if (disableAnswer) {
      return;
    }
    if (isMulti) {
      if (selectedAnswers.includes(v)) {
        setSelectedAnswers((s) => {
          s.splice(s.indexOf(v), 1);
          return [...s];
        });
      } else {
        setSelectedAnswers((s) => {
          s.push(v);
          return [...s];
        });
      }
    } else {
      setSelectedAnswers([v]);
    }
  };

  const handleInputChange = (e: any) => {
    setTextareaValue(e.target.value);
  };

  const handleTryAgain = () => {
    setShowCorrectAnswers(false);
    setSelectedAnswers([]);
  };

  const showAnswerButton =
    !disableAnswer &&
    !showCorrectAnswers &&
    !showStudentAnswers &&
    ((questionType === 'ABCDQuestion' && !!selectedAnswers.length) ||
      (questionType === 'FreeForm' && textareaValue));

  const getRealABCDEAnswers = (answer: any) => {
    const answers = answer
      .split(',')
      .map((a: string) => asnwerMap[a.trim()]?.answer)
      .join(', ');
    return answers;
  };

  const lastStudentAnswer = studentAnswers.at(-1);
  let aiGrade = {
    points: -1,
    suggestion: '',
  };
  if (lastStudentAnswer) {
    try {
      let aiGradeParsed = JSON.parse(
        lastStudentAnswer?.fields?.ai_grade?.replaceAll("'", '"'),
      );
      if (aiGradeParsed && aiGradeParsed.response) {
        aiGradeParsed = aiGradeParsed.response;
      }
      if (aiGradeParsed.points && aiGradeParsed.suggestion) {
        aiGrade = aiGradeParsed;
      }
    } catch (e) {
      console.log(e);
      aiGrade.suggestion = lastStudentAnswer?.fields?.ai_grade;
    }
  }

  return (
    <Box
      paddingTop={8}
      fontSize={isMobile ? 'sm' : 'xl'}
      className={`question-${contentData.unique_quiz_id}`}
    >
      <Text
        fontWeight="medium"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        dangerouslySetInnerHTML={{ __html: contentData.question }}
      ></Text>
      <Text fontSize="small" fontWeight="bold">
        ({contentData.max_points} pts.)
      </Text>
      {(isTeacher) && (
        <VStack spacing={0} justify="space-between">
          <Box w="full" display="flex" flexDirection="column" alignItems="flex-start">
            <Button
              mb="4"
              variant="ghost"
              color="blue"
              onClick={() => {
                setShowStudentAnswers(!showStudentAnswers);
              }}
            >
              {showStudentAnswers
                ? t('plugin.interactiveQuiz.hideStudentAnswers')
                : t('plugin.interactiveQuiz.showStudentAnswers')}
            </Button>
            <Button
              mb="4"
              variant="ghost"
              color="blue"
              onClick={() => {
                setShowAllSchools(!showAllSchools);
              }}
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              textAlign="left"
            >
              {showAllSchools
                ? t('plugin.interactiveQuiz.hideAllSchools')
                : t('plugin.interactiveQuiz.showAllSchools')}
            </Button>
            {showStudentAnswers && (
              <Select
                styles={{
                  container: (provided, state) => ({
                    ...provided,
                    minWidth: '350px',
                    textAlign: 'left',
                    // marginRight: '40% !important',
                    marginBottom: '1rem',
                    marginLeft: '1rem'
                  }),
                }}
                isClearable
                placeholder={t('plugin.interactiveQuiz.selectStudent')}
                noOptionsMessage={() => t('plugin.interactiveQuiz.noStudents')}
                options={studentOptions}
                value={studentOptions.find((o) => o.value === studentId)}
                onChange={(value) => {
                  if (value) setStudentId(value.value);
                  else setStudentId(null);
                }}
                menuPlacement="auto" // ticket ENG-699
                menuPortalTarget={document.body}
              />
            )}
          </Box>
        </VStack>
      )}

      {showStudentAnswers && (
        <VStack
          w="full"
          justifyContent="start"
          align="start"
          spacing={0}
          gap={4}
        >
          {studentAnswers.map(
            ({ fields: studentAnswer }: any, index: number) => {
              const student = students.find(
                (s) => s.pk === studentAnswer.student_id,
              );
              if (
                studentId != null &&
                student != null &&
                student.pk !== studentId
              ) {
                return;
              }
              if (!student) {
                return;
              }

              let answers =
                contentData.question_type === 'ABCDQuestion'
                  ? studentAnswer.answers.split(',').map((key: string) => {
                    const nkey = Number(key);
                    return nkey;
                  })
                  : studentAnswer.answers;
              return (
                <StudentAnswer
                  key={index}
                  isCorrect={studentAnswer.is_correct}
                  student={student}
                  answers={answers}
                  allAnswers={contentData.answers}
                  points={studentAnswer.points}
                  maxPoints={contentData.max_points}
                  date={new Date(studentAnswer.created_at)}
                  questionType={contentData.question_type}
                  manuallyGraded={studentAnswer.manually_graded}
                  getOldAnswers={getOldAnswers}
                  questionId={contentData.unique_quiz_id}
                  studentId={studentAnswer.student_id}
                  studyGroupId={studyGroup.pk}
                  answerId={studentAnswer.answer_id}
                />
              );
            },
          )}
        </VStack>
      )}
      {showStudentAnswers && studentAnswers.length === 0 && (
        <Text>{t('plugin.interactiveQuiz.noStudentAnswers')}</Text>
      )}

      {questionType === 'ABCDQuestion' && userAnswers.length ? (
        <>
          <Text fontWeight="bold">
            {t('plugin.interactiveQuiz.attempt')} {userAnswers.length}
            {repeatCount !== -1
              ? `${t('plugin.interactiveQuiz.of')} ${repeatCount}`
              : ''}
            <br />
            {t('plugin.interactiveQuiz.lastAnswer')} {` `}
            {checkStudentAnswers(
              studentAnswers[studentAnswers.length - 1],
              contentData.answers,
            )}
          </Text>
        </>
      ) : null}

      {questionType === 'FreeForm' &&
        isStaff && (!studyGroup?.pk || showAllSchools) && (
          <Box mt={8}>
            <AdminInteractiveQuizAnswers
              unique_quiz_id={contentData.unique_quiz_id}
              question_type={contentData.question_type}
              answers={contentData.answers}
              max_points={contentData.max_points}
              question_id={currentQuestionId}
            />
          </Box>)
      }

      {questionType === 'ABCDQuestion'
        ? !showStudentAnswers && (
          <VStack key={showCorrectAnswers ? '1' : '0'} alignItems="normal" spacing={3}>
            {isMulti && (
              <Text fontSize="sm" color="gray.600" fontStyle="italic">
                {t('plugin.interactiveQuiz.selectAllThatApply')}
              </Text>
            )}
         
            {Object.entries(contentData.answers).map(([key, value]: any) => {
              let isUserCorrect: boolean | null = null;
              let wasSelected: boolean | null = null;
              if (
                showCorrectAnswers &&
                studentAnswers[studentAnswers.length - 1]
              ) {
                wasSelected = studentAnswers[
                  studentAnswers.length - 1
                ].fields.answers
                  .split(',')
                  .includes(key);
                if (wasSelected && contentData.answers[key].isCorrect) {
                  isUserCorrect = true;
                } else if (
                  wasSelected &&
                  !contentData.answers[key].isCorrect
                ) {
                  isUserCorrect = false;
                } else if (
                  !wasSelected &&
                  contentData.answers[key].isCorrect
                ) {
                  isUserCorrect = false;
                }
              }
              return (
                <AnswerOption
                  key={key}
                  isSelected={selectedAnswers.includes(key)}
                  onClick={() => onChange(key)}
                  isUserCorrect={isUserCorrect}
                  wasSelected={wasSelected}
                  showFeedback={showCorrectAnswers}
                  isDisabled={showCorrectAnswers}
                  isMulti={isMulti}
                >
                  {value.answer}
                </AnswerOption>
              );
            })}
            {showCorrectAnswers && !disableAnswer && (
              <Button
                variant="outline"
                colorScheme="blue"
                mt={2}
                onClick={handleTryAgain}
              >
                {t('plugin.interactiveQuiz.tryAgain')}
              </Button>
            )}
            {isStaff && (!studyGroup?.pk || showAllSchools) && (
              <Box mt={8}>
                <AdminInteractiveQuizAnswers
                  unique_quiz_id={contentData.unique_quiz_id}
                  question_type={contentData.question_type}
                  answers={contentData.answers}
                  max_points={contentData.max_points}
                  question_id={currentQuestionId}
                />
              </Box>
            )}
          </VStack>
        )
        : !showStudentAnswers && (
          <div>
            <Textarea
              value={textareaValue}
              onChange={handleInputChange}
              placeholder={t('plugin.interactiveQuiz.writeYourAnswerHere')}
              isReadOnly={textareaDisabled}
              fontSize="md"
            />
            {aiGrade.suggestion && (
              <div>
                {aiGrade.points >= 0 && `${aiGrade.points}/100`}
                <Markdown markdown={aiGrade.suggestion} />
              </div>
            )}
          </div>
        )}
      {showAnswerButton && (
        <Button
          colorScheme="blue"
          mt={6}
          onClick={saveAnswer}
          isLoading={isSaving}
          disabled={userAnswers.length >= repeatCount && repeatCount !== -1}
        >
          {t('general.answer')}
        </Button>
      )}
      {contentData.use_ai_help &&
        content.children.map((child, index) => {
          if (index > 0) {
            return null;
          }
          if (child.plugin === PluginType.TriggerBlockPlugin) {
            return <TriggerBlock key={index} {...props} content={child} />;
          }
          return null;
        })}
    </Box>
  );
};

export default InteractiveQuiz;
